// Ejemplos con módulos de comunicación.
import { at, part, wires, UNO } from './helpers.js';

const BB = { id: 'bb1', type: 'breadboard', x: 100, z: -8, rot: 0, props: { size: 'half' } };

/** Agujero del riel de tierra de abajo (bn) en la misma columna que `col`. */
function gnd(col) {
  const idx = col - 2;
  return `bb1:bn${Math.floor(idx / 6) * 5 + (idx % 6) + 1}`;
}

// Conexión del RC522 al Uno por SPI (SDA = 10, RST = 9).
const RFID_WIRES = [
  ['rfid1:SDA', 'uno:10', 'yellow'],
  ['rfid1:SCK', 'uno:13', 'orange'],
  ['rfid1:MOSI', 'uno:11', 'blue'],
  ['rfid1:MISO', 'uno:12', 'purple'],
  ['rfid1:RST', 'uno:9', 'white'],
  ['rfid1:GND', 'uno:GND.1', 'black'],
  ['rfid1:3.3V', 'uno:3.3V', 'red'],
];

export const EXAMPLES = [
  {
    id: 'bluetooth-led',
    category: 'Comunicación',
    name: 'Bluetooth: LED desde el teléfono',
    description: 'Un HC-05 con SoftwareSerial: desde la terminal del panel envía 1 o 0 para encender o apagar el LED del pin 13.',
    project: {
      name: 'LED por Bluetooth',
      files: {
        'sketch.ino': `// Control del LED del pin 13 por Bluetooth (HC-05).
// Selecciona el módulo y escribe 1 o 0 en el «teléfono» del panel de propiedades.
#include <SoftwareSerial.h>

SoftwareSerial bt(10, 11);   // RX, TX del Arduino (TXD del módulo va al 10, RXD al 11)

void setup() {
  pinMode(13, OUTPUT);
  Serial.begin(9600);
  bt.begin(9600);
  bt.println("Listo. Envia 1 para encender y 0 para apagar.");
}

void loop() {
  if (bt.available()) {
    // SoftwareSerial no recibe mientras transmite: se lee la línea completa y luego se contesta.
    String orden = bt.readStringUntil('\\n');
    orden.trim();
    Serial.print("Recibido: ");
    Serial.println(orden);
    if (orden == "1") {
      digitalWrite(13, HIGH);
      bt.println("LED encendido");
    } else if (orden == "0") {
      digitalWrite(13, LOW);
      bt.println("LED apagado");
    } else {
      bt.println("No entiendo: " + orden);
    }
  }
}
`,
      },
      parts: [UNO, part('bt1', 'bluetooth-hc05', {}, { x: 52, z: -78, rot: 0 })],
      wires: wires([
        ['bt1:TXD', 'uno:10', 'green'],
        ['bt1:RXD', 'uno:11', 'yellow'],
        ['bt1:GND', 'uno:GND.1', 'black'],
        ['bt1:VCC', 'uno:5V', 'red'],
      ]),
    },
  },
  {
    id: 'rfid-acceso',
    category: 'Comunicación',
    name: 'RFID: control de acceso',
    description: 'El RC522 lee el UID de la tarjeta: la autorizada (la blanca) enciende el LED verde y cualquier otra (el llavero azul, la etiqueta NTAG213) el rojo. Haz clic en ella para acercarla al lector.',
    project: {
      name: 'Control de acceso RFID',
      files: {
        'sketch.ino': `// Control de acceso con un lector RFID RC522 (librería MFRC522).
// Haz clic en la tarjeta blanca (autorizada) o en el llavero azul o la etiqueta NTAG213 (no autorizadas) para acercarlos al lector.
#include <SPI.h>
#include <MFRC522.h>

MFRC522 rfid(10, 9);   // SDA (SS) y RST
const byte AUTORIZADA[4] = { 0x9A, 0x3B, 0x5C, 0x1D };
const int VERDE = 5;
const int ROJO = 6;

void setup() {
  Serial.begin(9600);
  SPI.begin();
  rfid.PCD_Init();
  pinMode(VERDE, OUTPUT);
  pinMode(ROJO, OUTPUT);
  Serial.println("Acerca una tarjeta al lector");
}

void loop() {
  if (!rfid.PICC_IsNewCardPresent() || !rfid.PICC_ReadCardSerial()) return;

  bool permitida = rfid.uid.size == 4;
  Serial.print("UID:");
  for (byte i = 0; i < rfid.uid.size; i++) {
    Serial.print(rfid.uid.uidByte[i] < 0x10 ? " 0" : " ");
    Serial.print(rfid.uid.uidByte[i], HEX);
    if (i < 4 && rfid.uid.uidByte[i] != AUTORIZADA[i]) permitida = false;
  }
  Serial.println(permitida ? "  -> acceso permitido" : "  -> acceso denegado");

  digitalWrite(permitida ? VERDE : ROJO, HIGH);
  delay(1500);
  digitalWrite(VERDE, LOW);
  digitalWrite(ROJO, LOW);
  rfid.PICC_HaltA();   // la tarjeta queda dormida hasta retirarla
}
`,
      },
      parts: [
        UNO,
        part('rfid1', 'rfid-rc522', {}, { x: -6, z: -96, rot: 0 }),
        BB,
        part('led1', 'led', { color: 'green' }, at(BB, 'f20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB, 'h21', 'resistor')),
        part('led2', 'led', { color: 'red' }, at(BB, 'f8', 'led')),
        part('r2', 'resistor', { value: 220 }, at(BB, 'h9', 'resistor')),
      ],
      wires: wires([
        ['rfid1:SDA', 'uno:10', 'yellow'],
        ['rfid1:SCK', 'uno:13', 'orange'],
        ['rfid1:MOSI', 'uno:11', 'blue'],
        ['rfid1:MISO', 'uno:12', 'purple'],
        ['rfid1:RST', 'uno:9', 'white'],
        ['rfid1:GND', 'uno:GND.1', 'black'],
        ['rfid1:3.3V', 'uno:3.3V', 'red'],
        ['uno:5', 'bb1:j25', 'green'],
        ['uno:6', 'bb1:j13', 'red'],
        ['bb1:j20', 'bb1:bn16', 'black'],
        ['bb1:j8', 'bb1:bn6', 'black'],
        ['bb1:bn1', 'uno:GND.3', 'black'],
      ]),
    },
  },
  {
    id: 'rfid-monedero',
    category: 'Comunicación',
    name: 'RFID: monedero con bloques de valor',
    description: 'El saldo vive en un bloque de valor de la tarjeta blanca: con la llave A solo se puede pagar (botón rojo o tecla P, pin 2) y recargar exige la llave B (botón azul o tecla R, pin 3). Acerca la tarjeta con clic.',
    project: {
      name: 'Monedero RFID',
      files: {
        'sketch.ino': `// Monedero en una tarjeta MIFARE Classic 1K con bloques de valor (RC522, librería MFRC522).
// Haz clic en la tarjeta blanca para acercarla. Botón rojo (pin 2, tecla P): PAGAR $3 con la llave A. Botón azul (pin 3, tecla R): RECARGAR $10 con la llave B.
// El saldo está en el bloque 4 (bloque de valor). Los bits de acceso del sector 1 hacen que la llave A solo pueda leer y
// descontar (decrementar): sumar saldo exige la llave B. La primera vez se prepara la tarjeta de fábrica con $20.
#include <SPI.h>
#include <MFRC522.h>

MFRC522 rfid(10, 9);
const byte BLOQUE_SALDO = 4, BLOQUE_TRAILER = 7;
const byte LLAVE_A[6] = { 0xA0, 0xA1, 0xA2, 0xA3, 0xA4, 0xA5 };   // consultar y pagar
const byte LLAVE_B[6] = { 0xB0, 0xB1, 0xB2, 0xB3, 0xB4, 0xB5 };   // recargar
const int PRECIO = 3, RECARGA = 10, SALDO_INICIAL = 20;
const int BOTON_PAGAR = 2, BOTON_RECARGAR = 3, VERDE = 5, ROJO = 6;

MFRC522::StatusCode entrar(byte tipoLlave, const byte* llave) {
  MFRC522::MIFARE_Key k;
  memcpy(k.keyByte, llave, 6);
  return rfid.PCD_Authenticate(tipoLlave, BLOQUE_SALDO, &k, &(rfid.uid));
}

// Despierta a la tarjeta que sigue sobre el lector (en HALT) o que volvió a IDLE tras un fallo, y la selecciona.
bool seleccionar() {
  byte atqa[2];
  byte n = sizeof(atqa);
  rfid.PCD_StopCrypto1();
  if (rfid.PICC_WakeupA(atqa, &n) != MFRC522::STATUS_OK) return false;
  return rfid.PICC_ReadCardSerial();
}

void terminar() {
  rfid.PICC_HaltA();
  rfid.PCD_StopCrypto1();
}

void aviso(int pin) {
  digitalWrite(pin, HIGH);
  delay(400);
  digitalWrite(pin, LOW);
}

// Tarjeta de fábrica: saldo inicial en el bloque 4 y llaves y bits de acceso propios en el trailer del sector 1.
bool preparar() {
  const byte FABRICA[6] = { 0xFF, 0xFF, 0xFF, 0xFF, 0xFF, 0xFF };
  if (entrar(MFRC522::PICC_CMD_MF_AUTH_KEY_A, FABRICA) != MFRC522::STATUS_OK) return false;
  if (rfid.MIFARE_SetValue(BLOQUE_SALDO, SALDO_INICIAL) != MFRC522::STATUS_OK) return false;
  byte trailer[16];
  memcpy(trailer, LLAVE_A, 6);
  memcpy(trailer + 10, LLAVE_B, 6);
  // Bloque 4: valor recargable con B (110). Bloque 5: solo lectura (010). Bloque 6: se escribe solo con B (100).
  // Sector trailer: las llaves y los bits de acceso solo se cambian con la llave B (011).
  rfid.MIFARE_SetAccessBits(&trailer[6], 6, 2, 4, 3);
  trailer[9] = 0x69;
  bool ok = rfid.MIFARE_Write(BLOQUE_TRAILER, trailer, 16) == MFRC522::STATUS_OK;
  terminar();
  return ok;
}

// Autentica con la llave A propia; si no entra, prueba la tarjeta como de fábrica y la prepara.
bool abrir() {
  if (entrar(MFRC522::PICC_CMD_MF_AUTH_KEY_A, LLAVE_A) == MFRC522::STATUS_OK) return true;
  if (!seleccionar() || !preparar()) return false;
  Serial.println("Tarjeta nueva: preparada con $20");
  return seleccionar() && entrar(MFRC522::PICC_CMD_MF_AUTH_KEY_A, LLAVE_A) == MFRC522::STATUS_OK;
}

void mostrarSaldo() {
  int32_t saldo;
  if (rfid.MIFARE_GetValue(BLOQUE_SALDO, &saldo) == MFRC522::STATUS_OK) {
    Serial.print("Saldo: $");
    Serial.println(saldo);
  }
}

void pagar() {
  int32_t saldo;
  if (rfid.MIFARE_GetValue(BLOQUE_SALDO, &saldo) != MFRC522::STATUS_OK) return;
  if (saldo < PRECIO) {
    Serial.println("Saldo insuficiente");
    aviso(ROJO);
    return;
  }
  // Decrementar deja el resultado en el buffer de la tarjeta; Transfer lo escribe en el bloque.
  if (rfid.MIFARE_Decrement(BLOQUE_SALDO, PRECIO) == MFRC522::STATUS_OK && rfid.MIFARE_Transfer(BLOQUE_SALDO) == MFRC522::STATUS_OK) {
    Serial.print("Pago de $");
    Serial.print(PRECIO);
    Serial.print(". ");
    mostrarSaldo();
    aviso(VERDE);
  } else {
    Serial.println("No se pudo pagar");
    aviso(ROJO);
  }
}

void recargar() {
  // La llave A no puede sumar saldo: hay que autenticar con la llave B.
  if (entrar(MFRC522::PICC_CMD_MF_AUTH_KEY_B, LLAVE_B) != MFRC522::STATUS_OK) {
    Serial.println("No se pudo entrar con la llave B");
    aviso(ROJO);
    return;
  }
  if (rfid.MIFARE_Increment(BLOQUE_SALDO, RECARGA) == MFRC522::STATUS_OK && rfid.MIFARE_Transfer(BLOQUE_SALDO) == MFRC522::STATUS_OK) {
    Serial.print("Recarga de $");
    Serial.print(RECARGA);
    Serial.print(". ");
    mostrarSaldo();
    aviso(VERDE);
  } else {
    Serial.println("No se pudo recargar");
    aviso(ROJO);
  }
}

void setup() {
  Serial.begin(9600);
  SPI.begin();
  rfid.PCD_Init();
  pinMode(BOTON_PAGAR, INPUT_PULLUP);
  pinMode(BOTON_RECARGAR, INPUT_PULLUP);
  pinMode(VERDE, OUTPUT);
  pinMode(ROJO, OUTPUT);
  Serial.println("Acerca la tarjeta; botones: pin 2 = pagar, pin 3 = recargar");
}

void loop() {
  bool pagarYa = digitalRead(BOTON_PAGAR) == LOW;
  bool recargarYa = digitalRead(BOTON_RECARGAR) == LOW;
  if (pagarYa || recargarYa) {
    if (!seleccionar()) {
      Serial.println("No hay tarjeta sobre el lector");
      aviso(ROJO);
    } else if (pagarYa) {
      if (abrir()) pagar();
      else aviso(ROJO);
    } else {
      recargar();
    }
    terminar();
    while (digitalRead(BOTON_PAGAR) == LOW || digitalRead(BOTON_RECARGAR) == LOW) delay(10);
    return;
  }
  // Tarjeta nueva: la prepara si es de fábrica y muestra su saldo.
  if (rfid.PICC_IsNewCardPresent() && rfid.PICC_ReadCardSerial()) {
    if (abrir()) mostrarSaldo();
    terminar();
  }
}
`,
      },
      parts: [
        UNO,
        part('rfid1', 'rfid-rc522', {}, { x: -6, z: -96, rot: 0 }),
        BB,
        part('btn1', 'pushbutton', { color: 'red', key: 'p' }, at(BB, 'e4', 'pushbutton')),
        part('btn2', 'pushbutton', { color: 'blue', key: 'r' }, at(BB, 'e10', 'pushbutton')),
        part('led1', 'led', { color: 'green' }, at(BB, 'f20', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB, 'h21', 'resistor')),
        part('led2', 'led', { color: 'red' }, at(BB, 'f14', 'led')),
        part('r2', 'resistor', { value: 220 }, at(BB, 'h15', 'resistor')),
      ],
      wires: wires([
        ...RFID_WIRES,
        ['uno:2', 'bb1:a4', 'red'],
        ['bb1:j6', gnd(6), 'black'],
        ['uno:3', 'bb1:a10', 'blue'],
        ['bb1:j12', gnd(12), 'black'],
        ['uno:5', 'bb1:j25', 'green'],
        ['bb1:j20', gnd(20), 'black'],
        ['uno:6', 'bb1:j19', 'orange'],
        ['bb1:j14', gnd(14), 'black'],
        ['bb1:bn1', 'uno:GND.3', 'black'],
      ]),
    },
  },
  {
    id: 'rfid-ntag',
    category: 'Comunicación',
    name: 'RFID: etiqueta NTAG213 con contador',
    description: 'Lee una etiqueta NFC NTAG213 (UID de 7 bytes, mensaje NDEF de texto) y guarda en ella un contador de visitas. Aleja la etiqueta y vuelve a acercarla para sumar.',
    project: {
      name: 'Etiqueta NTAG213',
      files: {
        'sketch.ino': `// Lee una etiqueta NFC NTAG213 con el RC522: UID de 7 bytes, tipo, capacidad, mensaje NDEF de texto y un contador
// de visitas que se guarda en la propia etiqueta (página 39, la última de usuario). Haz clic en la etiqueta para
// acercarla; aléjala y acércala otra vez para sumar una visita (la etiqueta conserva su memoria).
#include <SPI.h>
#include <MFRC522.h>

MFRC522 rfid(10, 9);
const byte PAGINA_CONTADOR = 39;

// Lee 4 páginas (16 bytes) desde 'pagina'.
bool leer(byte pagina, byte* destino) {
  byte buf[18];
  byte n = sizeof(buf);
  if (rfid.MIFARE_Read(pagina, buf, &n) != MFRC522::STATUS_OK) return false;
  memcpy(destino, buf, 16);
  return true;
}

// Recorre los TLV de las páginas 4 a 15 hasta el mensaje NDEF (03) y muestra el texto de su primer registro «T».
void mostrarMensaje() {
  byte datos[48];
  for (byte p = 4; p < 16; p += 4) {
    if (!leer(p, datos + (p - 4) * 4)) return;
  }
  byte i = 0;
  while (i < 44) {
    byte tlv = datos[i];
    if (tlv == 0x00) { i++; continue; }                    // relleno
    if (tlv == 0xFE) break;                                // fin de los TLV
    byte largo = datos[i + 1];
    if (tlv == 0x03) {                                     // mensaje NDEF: 03 largo D1 01 carga 54 estado idioma texto
      if (largo == 0) { Serial.println("Mensaje NDEF vacío"); return; }
      byte carga = datos[i + 4];
      byte idioma = datos[i + 6] & 0x3F;                   // estado: bits 0-5 = largo del código de idioma
      if (datos[i + 5] != 0x54) { Serial.println("El registro no es de texto"); return; }
      Serial.print("Mensaje: ");
      for (byte j = 7 + idioma; j < 6 + carga; j++) Serial.write(datos[i + j]);
      Serial.println();
      return;
    }
    i += 2 + largo;                                        // otro TLV: el de fábrica 01 03 A0 0C 34 es de bloqueos
  }
}

void setup() {
  Serial.begin(9600);
  SPI.begin();
  rfid.PCD_Init();
  Serial.println("Acerca la etiqueta NTAG213");
}

void loop() {
  if (!rfid.PICC_IsNewCardPresent() || !rfid.PICC_ReadCardSerial()) return;

  Serial.print("UID (");
  Serial.print(rfid.uid.size);
  Serial.print(" bytes):");
  for (byte i = 0; i < rfid.uid.size; i++) {
    Serial.print(rfid.uid.uidByte[i] < 0x10 ? " 0" : " ");
    Serial.print(rfid.uid.uidByte[i], HEX);
  }
  Serial.println();
  Serial.print("Tipo: ");
  Serial.println(rfid.PICC_GetTypeName(rfid.PICC_GetType(rfid.uid.sak)));

  byte pagina[16];
  if (leer(3, pagina)) {            // contenedor de capacidad: E1 = formato NFC Forum, byte 2 × 8 = bytes de usuario
    Serial.print("Capacidad: ");
    Serial.print(pagina[2] * 8);
    Serial.println(" bytes");
  }
  mostrarMensaje();

  if (leer(PAGINA_CONTADOR, pagina)) {
    unsigned long visitas = (unsigned long)pagina[0] | ((unsigned long)pagina[1] << 8);
    visitas++;
    byte nueva[4] = { (byte)(visitas & 0xFF), (byte)(visitas >> 8), 0, 0 };
    if (rfid.MIFARE_Ultralight_Write(PAGINA_CONTADOR, nueva, 4) == MFRC522::STATUS_OK) {
      Serial.print("Visita número ");
      Serial.println(visitas);
    }
  }
  Serial.println();
  rfid.PICC_HaltA();
}
`,
      },
      parts: [UNO, part('rfid1', 'rfid-rc522', {}, { x: -6, z: -96, rot: 0 })],
      wires: wires(RFID_WIRES),
    },
  },
  {
    id: 'rfid-varias',
    category: 'Comunicación',
    name: 'RFID: varias tarjetas a la vez',
    description: 'Acerca varias tarjetas con clic: la librería las separa por anticolisión, las lista una por una y los LED cuentan cuántas hay en el campo.',
    project: {
      name: 'Varias tarjetas RFID',
      files: {
        'sketch.ino': `// Lee varias tarjetas a la vez con anticolisión. Haz clic en la tarjeta blanca, el llavero y la etiqueta para acercarlas.
// Cada ronda apaga y enciende el campo (todas las tarjetas vuelven a empezar), separa una por una con
// PICC_IsNewCardPresent + PICC_ReadCardSerial (la librería resuelve las colisiones bit a bit) y duerme a cada una con
// PICC_HaltA para que la siguiente pregunta la conteste otra. Los LED de los pines 3, 4 y 5 cuentan las tarjetas halladas.
#include <SPI.h>
#include <MFRC522.h>

MFRC522 rfid(10, 9);
const int LEDS[3] = { 3, 4, 5 };
String anterior = "?";

String uidTexto() {
  String s;
  for (byte i = 0; i < rfid.uid.size; i++) {
    if (rfid.uid.uidByte[i] < 0x10) s += "0";
    s += String(rfid.uid.uidByte[i], HEX);
    if (i < rfid.uid.size - 1) s += " ";
  }
  s.toUpperCase();
  return s;
}

void setup() {
  Serial.begin(9600);
  SPI.begin();
  rfid.PCD_Init();
  for (int i = 0; i < 3; i++) pinMode(LEDS[i], OUTPUT);
}

void loop() {
  // Un campo nuevo despierta a todas en IDLE; una tarjeta dormida (HALT) solo vuelve con WUPA o al reiniciar el campo.
  rfid.PCD_AntennaOff();
  delay(5);
  rfid.PCD_AntennaOn();
  delay(5);

  String lista = "";
  int total = 0;
  while (rfid.PICC_IsNewCardPresent() && rfid.PICC_ReadCardSerial()) {
    total++;
    lista += "\\n  " + String(total) + ". " + uidTexto() + "  " + rfid.PICC_GetTypeName(rfid.PICC_GetType(rfid.uid.sak));
    rfid.PICC_HaltA();
  }
  for (int i = 0; i < 3; i++) digitalWrite(LEDS[i], i < total);

  if (lista != anterior) {   // solo avisa cuando cambia lo que hay en el campo
    anterior = lista;
    Serial.print("Tarjetas en el campo: ");
    Serial.print(total);
    Serial.println(lista);
  }
  delay(200);
}
`,
      },
      parts: [
        UNO,
        part('rfid1', 'rfid-rc522', {}, { x: -6, z: -96, rot: 0 }),
        BB,
        part('led1', 'led', { color: 'green' }, at(BB, 'f4', 'led')),
        part('r1', 'resistor', { value: 220 }, at(BB, 'h5', 'resistor')),
        part('led2', 'led', { color: 'yellow' }, at(BB, 'f12', 'led')),
        part('r2', 'resistor', { value: 220 }, at(BB, 'h13', 'resistor')),
        part('led3', 'led', { color: 'red' }, at(BB, 'f20', 'led')),
        part('r3', 'resistor', { value: 220 }, at(BB, 'h21', 'resistor')),
      ],
      wires: wires([
        ...RFID_WIRES,
        ['uno:3', 'bb1:j9', 'green'],
        ['bb1:j4', gnd(4), 'black'],
        ['uno:4', 'bb1:j17', 'yellow'],
        ['bb1:j12', gnd(12), 'black'],
        ['uno:5', 'bb1:j25', 'red'],
        ['bb1:j20', gnd(20), 'black'],
        ['bb1:bn1', 'uno:GND.3', 'black'],
      ]),
    },
  },
];
